import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
  OnDestroy
} from '@angular/core';
import { BaseComponent } from 'src/app/models/base/base-component';
import { ColumnListLayoutModifierClass } from 'src/app/models/enum/shared/column-list-layout-types';
import { SectionColumnConfigDataValue } from 'src/app/models/enum/shared/section-column-config-data-value';
import { SectionColumnType } from 'src/app/models/enum/shared/section-column-type';
import { BadgeAsset } from 'src/app/models/image/dto/badge-asset';
import { SectionColumnViewModel } from 'src/app/modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { SectionRowViewModel } from 'src/app/modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { HtmlUtils } from 'src/app/utils/html-utils';
import { environment } from '../../../../../environments/environment';

type Measurements = {
  containerWidth: number;
  colGapPx: number;
  widthsPx: number[];
  items: HTMLDivElement[];
};

@Component({
  selector: 'app-column-list-items',
  templateUrl: './column-list-items.component.html',
  styleUrls: ['./column-list-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ColumnListItemsComponent extends BaseComponent implements OnDestroy {
  private observer: IntersectionObserver | undefined;
  @ViewChild('listgrid') listgrid: ElementRef<HTMLElement>;
  @Input({ required: true }) colVm: SectionColumnViewModel;
  @Input({ required: true }) rowVm: SectionRowViewModel;
  @Input({ required: true }) displayFormat: SectionColumnConfigDataValue;
  @Input() columnListLayoutModifierClass: ColumnListLayoutModifierClass;
  @Input({ required: true }) sectionColumnType: SectionColumnType;
  @Input({ required: true }) parentColumnElement: HTMLDivElement;
  setupViews() {}

  async setupBindings() {
    await HtmlUtils.fdp.mutate(() => this.addHasListGridContainerClassToParent(this.listgrid.nativeElement));
    const productContainer: HTMLDivElement = this.listgrid.nativeElement.closest('.product-container');
    const zoomLevel = await HtmlUtils.getZoomLevel(productContainer);
    requestAnimationFrame(() => this.hidePartiallyVisibleListBadgeItems(zoomLevel));
    if (!this.colVm.columnConfigDataValueIsBadgeMode()) {
      await document.fonts.ready;
      await HtmlUtils.fdp.mutate(() => this.constrain(this.listgrid.nativeElement));
      const { willOverflowX, items, numCols, colGap } = await this.layoutGridAndPrune(this.listgrid.nativeElement);
      await this.resolveIncompleteLastGridRow(numCols, colGap, items);
      await HtmlUtils.fdp.mutate(() => this.unconstrain(this.listgrid.nativeElement));
      await this.alignContent(this.listgrid.nativeElement, willOverflowX);
    }
  }

  narrowListItemTypeToBadgeAsset(listItem: BadgeAsset | string | undefined): BadgeAsset | undefined {
    return typeof listItem === 'string' ? undefined : listItem;
  }

  narrowListItemTypeToString(listItem: BadgeAsset | string | undefined): string | undefined {
    return typeof listItem === 'string' ? listItem : undefined;
  }

  listItemIsString(listItem: BadgeAsset | string | undefined): listItem is string {
    return typeof listItem === 'string';
  }

  /**
   * Assumptions:
   * - badges are in a single row with no wrapping
   * - parent element has no margin/padding affecting available width
   */
  private async hidePartiallyVisibleListBadgeItems(zoomLevel: number) {
    if (!this.colVm.columnConfigDataValueIsBadgeMode()) return;
    const listGridElement = this.listgrid.nativeElement;
    const parent = this.parentColumnElement;
    if (!parent) return;
    const children = Array.from(listGridElement.querySelectorAll<HTMLElement>('.list-grid-item'));
    if (!children.length) return;
    const childrenToHide = await this.childBadgesToHide(listGridElement, children, zoomLevel);
    await HtmlUtils.fdp.mutate(() => {
      childrenToHide.forEach(ch => ch.classList.add('intersect-hide'));
      if (!environment.production && childrenToHide.length) {
        console.warn(`Badge(s) hidden due to overflow in column '${this.sectionColumnType}'`);
      }
    });
  }

  private async childBadgesToHide(
    listGridElement: HTMLElement,
    children: HTMLElement[],
    zoomLevel: number
  ): Promise<HTMLElement[]> {
    return HtmlUtils.fdp.measure(() => {
      const firstChildWidth = children[0].getBoundingClientRect().width;
      const parentColumnWidth = this.parentColumnElement?.getBoundingClientRect()?.width ?? 0;
      const parsedColGap = parseFloat(getComputedStyle(listGridElement).columnGap);
      const colGapPx = (Number.isFinite(parsedColGap) ? parsedColGap : 0) * zoomLevel;
      if (!(firstChildWidth > 0) || !(parentColumnWidth > 0)) return;
      let maxElementsThatCanFit = Math.floor(parentColumnWidth / firstChildWidth);
      maxElementsThatCanFit = Math.max(0, Math.min(maxElementsThatCanFit, children.length));
      const numGaps = Math.max(0, maxElementsThatCanFit - 1);
      const totalCandidateWidth = firstChildWidth * maxElementsThatCanFit + numGaps * colGapPx;
      let resolvedMax =
        totalCandidateWidth <= parentColumnWidth ? maxElementsThatCanFit : Math.max(0, maxElementsThatCanFit - 1);
      resolvedMax = Math.min(resolvedMax, children.length);
      return children.slice(resolvedMax);
    });
  }

  private addHasListGridContainerClassToParent(listGridContainer: HTMLElement): void {
    const general: HTMLDivElement = listGridContainer.closest('.product-column-general');
    if (general) general.classList.add('has-list-grid-container');
  }

  private getColumnGapPx(listGridContainer: HTMLElement): number {
    const cStyle: CSSStyleDeclaration = getComputedStyle(listGridContainer);
    const colGap: string = cStyle.columnGap && cStyle.columnGap !== 'normal' ? cStyle.columnGap : cStyle.gap;
    return HtmlUtils.pxToNumber(colGap);
  }

  private getRectToClientScale(element: HTMLElement): number {
    const clientWidth = element.clientWidth;
    const rectWidth = element.getBoundingClientRect().width;
    return rectWidth > 0 && clientWidth > 0 ? rectWidth / clientWidth : 1;
  }

  private applyGridTemplateColumns(listGridContainer: HTMLElement, maxWidthPerCol: number[]): void {
    listGridContainer.style.gridTemplateColumns = maxWidthPerCol.map(w => `${w}px`).join(' ');
  }

  private constrain(listGridContainer: HTMLElement): void {
    listGridContainer.style.setProperty('height', '100%', 'important');
    const general: HTMLDivElement = listGridContainer.closest('.product-column-general');
    if (general) {
      general.style.setProperty('height', '100%', 'important');
      general.style.setProperty('display', 'unset', 'important');
      const productColumn: HTMLDivElement = general.closest('.product-column');
      if (productColumn) {
        productColumn.style.setProperty('height', '100%', 'important');
      }
    }
  }

  private unconstrain(listGridContainer: HTMLElement): void {
    listGridContainer.style.removeProperty('height');
    const general: HTMLDivElement = listGridContainer.closest('.product-column-general');
    if (general) {
      general.style.removeProperty('height');
      general.style.removeProperty('display');
      const productColumn: HTMLDivElement = general.closest('.product-column');
      if (productColumn) {
        productColumn.style.removeProperty('height');
      }
    }
  }

  private async layoutGridAndPrune(
    listGridContainer: HTMLElement,
    maxIters: number = 3
  ): Promise<{ willOverflowX: boolean; items: HTMLDivElement[]; numCols: number; colGap: number }> {
    const result = {
      willOverflowX: false,
      items: [],
      numCols: 0,
      colGap: 0
    };
    for (let iter = 0; iter < maxIters; iter++) {
      const measurements = await this.gridMeasurements(listGridContainer);
      if (!measurements || !measurements.widthsPx.length) break;
      const { maxWidthPerCol, willOverflowX } = HtmlUtils.findBestColumns(
        measurements.widthsPx,
        measurements.containerWidth,
        measurements.colGapPx
      );
      const numCols = maxWidthPerCol.length;
      if (numCols === 0) break;
      await HtmlUtils.fdp.mutate(() => this.applyGridTemplateColumns(listGridContainer, maxWidthPerCol));
      const firstOverflowIndex = await this.gridFirstOverflowIndex(listGridContainer, measurements);
      result.willOverflowX = willOverflowX;
      result.items = measurements.items;
      result.numCols = numCols;
      result.colGap = measurements.colGapPx;
      if (firstOverflowIndex === -1) break;
      const pruned = await this.nItemsPrunedFromGrid(firstOverflowIndex, measurements);
      if (pruned === 0) break;
    }
    return result;
  }

  private async alignContent(listGridContainer: HTMLElement, willOverflowX: boolean) {
    if (!willOverflowX) return;
    await HtmlUtils.fdp.mutate(() => {
      const general: HTMLDivElement = listGridContainer.closest('.product-column-general');
      general?.style.setProperty('justify-content', 'start', 'important');
    });
  }

  private gridMeasurements(listGridContainer: HTMLElement): Promise<Measurements | null> {
    return HtmlUtils.fdp.measure(() => {
      const containerWidth: number = Math.floor(listGridContainer.clientWidth);
      if (!containerWidth) return null;
      const rectToClientScale = this.getRectToClientScale(listGridContainer);
      const colGapPx: number = this.getColumnGapPx(listGridContainer);
      const spans: HTMLSpanElement[] = Array.from(listGridContainer.querySelectorAll('.list-grid-text-item'));
      const rawWidthsPx: number[] = spans.map(s => s.getBoundingClientRect().width);
      const widthsPx: number[] = rawWidthsPx.map(w => Math.ceil(w / rectToClientScale));
      const items: HTMLDivElement[] = Array.from(listGridContainer.querySelectorAll('.list-grid-item'));
      return {
        containerWidth: containerWidth,
        colGapPx: colGapPx,
        widthsPx: widthsPx,
        items
      } as Measurements;
    });
  }

  private gridFirstOverflowIndex(listGridContainer: HTMLElement, measurements: Measurements): Promise<number> {
    return HtmlUtils.fdp.measure(() => {
      const containerRect: DOMRect = listGridContainer.getBoundingClientRect();
      for (let i = 0; i < measurements.items.length; i++) {
        if (measurements.items[i].getBoundingClientRect().bottom > containerRect.bottom) return i;
      }
      return -1;
    });
  }

  private nItemsPrunedFromGrid(firstOverflowIndex: number, measurements: Measurements): Promise<number> {
    return HtmlUtils.fdp.mutate(() => {
      let count = 0;
      for (let i = measurements.items.length - 1; i >= firstOverflowIndex; i--) {
        const toRemove = measurements.items[i];
        toRemove.remove();
        count++;
        if (!environment.production) {
          console.warn(
            `Element with text content '${toRemove.textContent?.trim()}' ` +
              `hidden due to overflow in column '${this.sectionColumnType}'`
          );
        }
      }
      return count;
    });
  }

  private async resolveIncompleteLastGridRow(
    numCols: number,
    colGap: number,
    gridItems: HTMLDivElement[]
  ): Promise<void> {
    if (numCols <= 0 || !gridItems || gridItems.length === 0) return;
    const numItems = gridItems.length;
    const incompleteRowItemCount = numItems % numCols;
    if (incompleteRowItemCount === 0) return;
    const firstIncompleteRowItemIndex = numItems - incompleteRowItemCount;
    const incompleteRowItems = gridItems.slice(firstIncompleteRowItemIndex);
    const remainingRectangularGridItems = gridItems.slice(0, firstIncompleteRowItemIndex);
    const gridContainer = this.listgrid.nativeElement;
    const resolvedMaxWidthsPerCol = await HtmlUtils.fdp.measure(() => {
      const rectToClientScale = this.getRectToClientScale(gridContainer);
      const remainingWidthsPx: number[] = [];
      for (const item of remainingRectangularGridItems) {
        const span = item.querySelector<HTMLSpanElement>('.list-grid-text-item');
        if (!span) continue;
        remainingWidthsPx.push(Math.ceil(span.getBoundingClientRect().width / rectToClientScale));
      }
      return HtmlUtils.computeColumnWidthsAndTotal(remainingWidthsPx, numCols, colGap).maxWidthPerCol;
    });
    await HtmlUtils.fdp.mutate(() => {
      // This means "find an .incomplete-grid-row that is a direct child of gridContainer"
      gridContainer.querySelector(':scope > .incomplete-grid-row')?.remove();
      for (const item of incompleteRowItems) {
        item.remove();
      }
      this.applyGridTemplateColumns(gridContainer, resolvedMaxWidthsPerCol);
      const flexGridSpanDiv = document.createElement('div');
      flexGridSpanDiv.className = 'incomplete-grid-row';
      Object.assign(flexGridSpanDiv.style, {
        display: 'flex',
        gridColumn: `span ${numCols}`,
        gap: `${colGap}px`,
        width: '100%'
      });
      for (const item of incompleteRowItems) {
        flexGridSpanDiv.appendChild(item);
      }
      gridContainer.appendChild(flexGridSpanDiv);
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.observer?.disconnect();
  }
}
